import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import PrizeDrawsTable from 'components/Dashboard/WorldCup/PrizeDraws/PrizeDrawsTable';
import PrizeDrawsFilter from 'components/Dashboard/WorldCup/PrizeDraws/PrizeDrawsFilter';
import PrizeDrawsConfigModal from 'components/Dashboard/WorldCup/PrizeDraws/PrizeDrawsConfigModal';
import PrizeDrawsDetailModal from 'components/Dashboard/WorldCup/PrizeDraws/PrizeDrawsDetailModal';
import PrizeDrawsRoulette from 'components/Dashboard/WorldCup/PrizeDraws/PrizeDrawsRoulette';
import Loading from 'components/Basic/Loading';
import CampaignSelector from 'components/Dashboard/WorldCup/CampaignSelector';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetPrizeDrawsListQuery } from 'core/worldCup/prizeDraws/PrizeDrawsService';
import { setPrizeDrawsData } from 'core/worldCup/prizeDraws/PrizeDrawsSlice';

type ViewMode = 'list' | 'roulette';

const PrizeDraws: React.FC = () => {
  const [state, setState] = useState({
    isLoading: false,
    isShowConfigModal: false,
    isShowDetailModal: false,
    searchText: '',
    sortStatus: '',
    filterStatus: '',
    sortName: '',
    sortId: 'desc',
    sortDate: '',
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedDrawId, setSelectedDrawId] = useState<number | null>(null);
  const [rouletteDrawId, setRouletteDrawId] = useState<number | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);

  const dispatch = useAppDispatch();
  const [getPrizeDrawsList] = useLazyGetPrizeDrawsListQuery();

  const { list } = useAppSelector(state => state.prizeDrawsReducer);

  useEffect(() => {
    getInitialData();
    setState({ ...state, searchText: '' });
  }, []);

  useEffect(() => {
    if (selectedCampaignId) {
      setCurrent(1);
      getInitialData();
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  const getInitialData = async () => {
    setState({ ...state, isLoading: true });

    const queryString = buildQueryString({
      search: '',
      page: 1,
      size: pageSize,
      sort_by_id: state.sortId,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getPrizeDrawsList({ queryString }).unwrap();
      dispatch(setPrizeDrawsData({ list: res }));
    } catch (error) {
      console.error('Error fetching prizeDraws list:', error);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  const handleChangePage = async (page: number, size: number) => {
    setState(prev => ({ ...prev, isLoading: true }));
    setPageSize(size);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: page,
      size: size,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      sort_by_date: state.sortDate,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getPrizeDrawsList({ queryString }).unwrap();
      dispatch(setPrizeDrawsData({ list: res }));
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (e) {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setState({ ...state, searchText: searchTextValue });
    if (e.keyCode === 13) {
      setCurrent(1);

      const queryString = buildQueryString({
        search: e.target.value.trim(),
        page: 1,
        size: pageSize,
        sort_by_status: state.sortStatus,
        sort_by_id: state.sortId,
        sort_by_name: state.sortName,
        sort_by_date: state.sortDate,
        filter: state.filterStatus,
        campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
      });

      try {
        const res = await getPrizeDrawsList({ queryString }).unwrap();
        dispatch(setPrizeDrawsData({ list: res }));
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleAvailableFilter = (value: string) => {
    setState({ ...state, filterStatus: value });
  };

  const handleSort = (value: string) => {
    let newState = { ...state };
    if (value[0] === 'I') {
      newState = {
        ...newState,
        sortId: value.substring(1),
        sortName: '',
        sortStatus: '',
        sortDate: '',
      };
    } else if (value[0] === 'N') {
      newState = {
        ...newState,
        sortName: value.substring(1),
        sortId: '',
        sortStatus: '',
        sortDate: '',
      };
    } else if (value[0] === 'S') {
      newState = {
        ...newState,
        sortStatus: value.substring(1),
        sortId: '',
        sortName: '',
        sortDate: '',
      };
    } else if (value[0] === 'D') {
      newState = {
        ...newState,
        sortDate: value.substring(1),
        sortId: '',
        sortName: '',
        sortStatus: '',
      };
    }
    setState(newState);
  };

  const onApplyFilter = async () => {
    setState({ ...state, isLoading: true });
    setCurrent(1);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: 1,
      size: pageSize,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      sort_by_date: state.sortDate,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getPrizeDrawsList({ queryString }).unwrap();
      dispatch(setPrizeDrawsData({ list: res }));
    } catch (e) {
      console.log(e);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  const handleViewDetail = (id: number) => {
    setSelectedDrawId(id);
    setState({ ...state, isShowDetailModal: true });
  };

  const handleStartRoulette = (id: number) => {
    setRouletteDrawId(id);
    setViewMode('roulette');
  };

  const handleBackFromRoulette = () => {
    setViewMode('list');
    setRouletteDrawId(null);
    getInitialData();
  };

  if (viewMode === 'roulette' && rouletteDrawId) {
    return (
      <PrizeDrawsRoulette
        prizeDrawId={rouletteDrawId}
        onBack={handleBackFromRoulette}
      />
    );
  }

  return (
    <div className="prizeDraws_wrapper" id="prizeDraws_wrapper" style={{ position: 'relative' }}>
      <Loading visible={state.isLoading} />
      <CampaignSelector
        selectedCampaignId={selectedCampaignId}
        onSelect={setSelectedCampaignId}
      />
      <div className="btn_wrapper" id="new_prizeDraw_btn_wrapper">
        <Button
          className="add_btn"
          onClick={() => setState({ ...state, isShowConfigModal: true })}
          id="new_prizeDraw_btn"
        >
          <FormattedMessage id="Configure prize draw" />
          <ArrowForward />
        </Button>
      </div>
      <PrizeDrawsFilter
        searchText={state.searchText}
        handleSearch={handleSearch}
        onAvailableFilter={handleAvailableFilter}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
      />
      <PrizeDrawsTable
        prizeDrawsData={list.data || null}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.meta?.totalCount || 0}
        setCurrent={setCurrent}
        current={current}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onViewDetail={handleViewDetail}
        onStartRoulette={handleStartRoulette}
      />
      <PrizeDrawsConfigModal
        mode="fullscreen"
        isOpenModal={state.isShowConfigModal}
        onClose={() => setState({ ...state, isShowConfigModal: false })}
        onSaveSuccess={() => handleChangePage(current, pageSize)}
        campaignId={selectedCampaignId}
      />
      <PrizeDrawsDetailModal
        isOpenModal={state.isShowDetailModal}
        prizeDrawId={selectedDrawId}
        onCloseModal={() => {
          setState({ ...state, isShowDetailModal: false });
          setSelectedDrawId(null);
        }}
      />
    </div>
  );
};

export default PrizeDraws;

const buildQueryString = ({
  search = '',
  page = 1,
  size = 10,
  sort_by_id = 'desc',
  sort_by_status = '',
  sort_by_name = '',
  sort_by_date = '',
  filter = '',
  campaignId = '',
}) => {
  const params = [`search=${encodeURIComponent(search)}`, `page=${page}`, `limit=${size}`];

  if (campaignId) {
    params.push(`campaignId=${campaignId}`);
  }

  if (sort_by_status) {
    params.push(`sort=status`);
    params.push(`sortDirection=${sort_by_status}`);
  }

  if (sort_by_id) {
    params.push(`sort=id`);
    params.push(`sortDirection=${sort_by_id}`);
  }

  if (sort_by_name) {
    params.push(`sort=name`);
    params.push(`sortDirection=${sort_by_name}`);
  }

  if (sort_by_date) {
    params.push(`sort=createdAt`);
    params.push(`sortDirection=${sort_by_date}`);
  }

  if (filter) {
    params.push(`status=${filter}`);
  }

  return params.join('&');
};
